/**
 * Client sync queue driver (ADR-0004): FIFO push in batches of up to
 * `MAX_PUSH_BATCH`, one in-flight push at a time; pull-since-cursor looping
 * while `hasMore`; exponential backoff with full jitter on retryable
 * failures; `reauth_required` stops the queue without dropping or
 * retry-spinning; a batch rejected with 400 is split in half once, and an
 * event rejected twice is quarantined so it never blocks the queue behind
 * it.
 *
 * This module performs I/O (via `SyncTransportPort`/`EventCachePort`), so it
 * lives in `src/data`, not `src/domain` (ADR-0001 rule 2/3).
 */

import { MAX_PUSH_BATCH, MAX_PULL_LIMIT } from '../../contracts';
import type { CompassEvent, EventCachePort, EventId, SyncTransportPort } from '../../contracts';
import { dedupeEvents } from '../../domain/event-log/event-log';
import {
  InvalidRequestError,
  ReauthRequiredError,
  RetryableSyncError,
} from './http-sync-transport';

export type SyncQueueState = 'idle' | 'syncing' | 'offline_queued' | 'reauth_required' | 'error';

export interface SyncQueueStatus {
  readonly state: SyncQueueState;
  readonly pendingEvents: number;
  readonly lastSyncedAt: string | null;
}

export interface SyncQueueDriverOptions {
  readonly cache: EventCachePort;
  readonly transport: SyncTransportPort;
  /** Overridable for tests; defaults to real timers via setTimeout. */
  readonly sleep?: (ms: number) => Promise<void>;
  /** Overridable source of randomness for jitter, defaults to Math.random. */
  readonly random?: () => number;
  readonly now?: () => string;
}

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 60_000;

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface SyncQueueDriver {
  /** Push everything currently queued, looping batches until the queue is empty or a stop condition is hit. */
  pushAll(): Promise<void>;
  /** Pull from the stored cursor, looping while hasMore, integrating into the cache. */
  pullAll(): Promise<void>;
  /** Convenience: pushAll then pullAll -- the normal "sync now" operation. */
  syncNow(): Promise<void>;
  getStatus(): SyncQueueStatus;
}

export function createSyncQueueDriver(options: SyncQueueDriverOptions): SyncQueueDriver {
  const { cache, transport } = options;
  const sleep = options.sleep ?? defaultSleep;
  const random = options.random ?? Math.random;
  const now = options.now ?? (() => new Date().toISOString());

  let state: SyncQueueState = 'idle';
  let lastSyncedAt: string | null = null;
  let pendingSnapshot = 0;

  /**
   * `attempt` is 1-based (the first retry after an initial failure).
   * Matches ADR-0004's schedule exactly: attempt 1 -> cap 1s, attempt 2 ->
   * cap 2s, attempt 3 -> cap 4s, ... capped at 60s. Full jitter: a uniform
   * random delay in [0, cap], reset on success or a connectivity event (the
   * caller resets `attempt` to 0 after every successful batch).
   */
  function backoffDelay(attempt: number): number {
    const cap = Math.min(MAX_BACKOFF_MS, INITIAL_BACKOFF_MS * 2 ** (attempt - 1));
    return random() * cap;
  }

  async function pushBatch(batch: readonly CompassEvent[]): Promise<void> {
    if (batch.length === 0) return;

    try {
      const response = await transport.push(batch);
      const confirmed = [...response.accepted, ...response.duplicates];
      await cache.markPushed(confirmed);
      return;
    } catch (error) {
      if (error instanceof ReauthRequiredError) {
        state = 'reauth_required';
        throw error;
      }

      if (error instanceof InvalidRequestError) {
        if (batch.length === 1) {
          // A single event rejected: quarantine it so it never blocks the
          // queue behind it (ADR-0004).
          const only = batch[0];
          if (only) await cache.quarantine(only.eventId);
          return;
        }
        // Split in half once to isolate a poison event; each half is
        // retried through the same path (which will itself split again if
        // still too large, converging to single-event quarantine).
        const mid = Math.ceil(batch.length / 2);
        await pushBatch(batch.slice(0, mid));
        await pushBatch(batch.slice(mid));
        return;
      }

      if (error instanceof RetryableSyncError) {
        throw error;
      }

      throw error;
    }
  }

  /**
   * Safety bound on "batch accepted without error, but the queue didn't
   * shrink" -- a well-behaved server (worker-api.ts: every pushed event ends
   * up in `accepted` or `duplicates`) can never trigger this, but a
   * misbehaving/misconfigured transport that returns success without
   * actually confirming every event would otherwise spin this loop at full
   * speed forever (no error is thrown, so the backoff-on-error path never
   * engages). Chosen generously above any real batch-of-batches scenario
   * (MAX_PUSH_BATCH per iteration) so it never fires in normal operation.
   */
  const MAX_NO_PROGRESS_ITERATIONS = 1000;

  async function pushAll(): Promise<void> {
    pendingSnapshot = await cache.pendingCount();
    if (pendingSnapshot === 0) {
      state = 'idle';
      return;
    }

    state = 'syncing';
    let attempt = 0;
    let noProgressIterations = 0;
    let previousPending = pendingSnapshot;

    while (true) {
      const queued = await cache.peekQueue(MAX_PUSH_BATCH);
      if (queued.length === 0) break;

      try {
        await pushBatch(queued);
        attempt = 0;
        pendingSnapshot = await cache.pendingCount();
        if (pendingSnapshot === 0) break;

        if (pendingSnapshot >= previousPending) {
          noProgressIterations += 1;
          if (noProgressIterations >= MAX_NO_PROGRESS_ITERATIONS) {
            // The server/transport claimed success without the queue
            // actually shrinking -- stop rather than spin forever; surfaced
            // via SyncStatusView as a stalled 'error' state.
            state = 'error';
            return;
          }
        } else {
          noProgressIterations = 0;
        }
        previousPending = pendingSnapshot;
      } catch (error) {
        if (error instanceof ReauthRequiredError) {
          // Stop the queue entirely -- do not retry-spin (ADR-0004).
          return;
        }
        // Network failure / timeout / 5xx / 429: retry with backoff.
        attempt += 1;
        state = 'offline_queued';
        await sleep(backoffDelay(attempt));
        continue;
      }
    }

    lastSyncedAt = now();
    state = 'idle';
  }

  async function pullAll(): Promise<void> {
    state = state === 'reauth_required' ? state : 'syncing';
    let cursor = await cache.getCursor();

    while (true) {
      let response;
      try {
        response = await transport.pull(cursor, MAX_PULL_LIMIT);
      } catch (error) {
        if (error instanceof ReauthRequiredError) {
          state = 'reauth_required';
          return;
        }
        // Retryable failures during pull: stop for this call; the next
        // triggered sync (foreground trigger, ADR-0004) will retry from the
        // same cursor -- pull is idempotent (dedupe by eventId on
        // integrate), so no data is lost by giving up here.
        state = 'error';
        return;
      }

      const events = dedupeEvents(response.events.map((synced) => synced.event));
      await cache.integrateRemote(events);
      await cache.setCursor(response.nextCursor);
      cursor = response.nextCursor;

      if (!response.hasMore) break;
    }

    lastSyncedAt = now();
    if (state !== 'reauth_required') state = 'idle';
  }

  async function syncNow(): Promise<void> {
    await pushAll();
    if (state === 'reauth_required') return;
    await pullAll();
  }

  function getStatus(): SyncQueueStatus {
    return { state, pendingEvents: pendingSnapshot, lastSyncedAt };
  }

  return { pushAll, pullAll, syncNow, getStatus };
}

export type { EventId };
